import * as React from "react";
import { Alert, Button, Divider } from "antd";
import { formSetValue, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { CpSwitch } from "../../../gvc/testNewForm/CpSwitch";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpErrorWarning } from "../../../gvc/testNewForm/CpErrorWarning";
import { CpInput } from "../../../gvc/testNewForm/CpInput";
import { CpTooltip } from "../../../gvc/testNewForm/CpTooltip";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";

// TODO
// includeNamespaces: RegularExpression, // means "all" if missing
// excludeNamespaces: RegularExpression, // means non if missing

interface Props {}
export const AddonMetrics: React.FC<Props> = ({}) => {
  const isEnabled = useCpFormValue(`addons.metrics.isEnabled`);
  const isScrapeAnnotatedEnabled = useCpFormValue(`addons.metrics.scrapeAnnotated.isEnabled`);
  const kubeState = useCpFormValue(`addons.metrics.kubeState`);
  const nodeExporter = useCpFormValue(`addons.metrics.nodeExporter`);
  const cadvisor = useCpFormValue(`addons.metrics.cadvisor`);
  const { id } = useCpFormContext();

  if (!isEnabled) {
    return null;
  }

  const dashboardMetricsMissing = !kubeState || !nodeExporter || !cadvisor;

  function handleEnableDashboardMetrics() {
    formSetValue(id, `addons.metrics.kubeState`, true);
    formSetValue(id, `addons.metrics.nodeExporter`, true);
    formSetValue(id, `addons.metrics.cadvisor`, true);
  }

  return (
    <>
      <Divider orientation="left" orientationMargin={0}>
        Add-On | Metrics
      </Divider>
      <div id={`addon-metrics`} style={{ scrollMarginTop: 200 }}>
        <Alert
          className="mb-4"
          style={{ width: "fit-content" }}
          type={dashboardMetricsMissing ? "warning" : "success"}
          showIcon
          message={
            dashboardMetricsMissing ? (
              <div className="flex items-center gap-2">
                <span>
                  Kube State, Node Exporter, and cAdvisor are required for the mk8s dashboard to work correctly.
                </span>
                <Button size="small" type="primary" onClick={handleEnableDashboardMetrics}>
                  Enable Metrics and Required Flags
                </Button>
              </div>
            ) : (
              "Kube State, Node Exporter, and cAdvisor are enabled. Dashboard metrics requirements are satisfied."
            )
          }
        />
        <CpFormItem>
          <div className="flex items-center gap-2">
            <CpLabel name={`addons.metrics.coreDns`}>Core DNS</CpLabel>
            <CpSwitch name={`addons.metrics.coreDns`} />
            <CpTooltip tooltipProps={{ title: `Enable scraping of core-dns service` }} />
          </div>
        </CpFormItem>
        <CpFormItem>
          <div className="flex items-center gap-2">
            <CpLabel name={`addons.metrics.kubelet`}>Kubelet</CpLabel>
            <CpSwitch name={`addons.metrics.kubelet`} />
            <CpTooltip tooltipProps={{ title: `Enable scraping kubelet stats` }} />
          </div>
        </CpFormItem>
        <CpFormItem>
          <div className="flex items-center gap-2">
            <CpLabel name={`addons.metrics.kubeState`}>Kube State</CpLabel>
            <CpSwitch name={`addons.metrics.kubeState`} />
            <CpTooltip tooltipProps={{ title: `Enable kube-state metrics` }} />
          </div>
        </CpFormItem>
        <CpFormItem>
          <div className="flex items-center gap-2">
            <CpLabel name={`addons.metrics.nodeExporter`}>Node Exporter</CpLabel>
            <CpSwitch name={`addons.metrics.nodeExporter`} />
            <CpTooltip
              tooltipProps={{ title: `Enable collecting node-level stats (disk, network, filesystem, etc)` }}
            />
          </div>
        </CpFormItem>
        <CpFormItem>
          <div className="flex items-center gap-2">
            <CpLabel name={`addons.metrics.apiserver`}>API Server</CpLabel>
            <CpSwitch name={`addons.metrics.apiserver`} />
            <CpTooltip tooltipProps={{ title: `Enable scraping apiserver stats` }} />
          </div>
        </CpFormItem>
        <CpFormItem>
          <div className="flex items-center gap-2">
            <CpLabel name={`addons.metrics.cadvisor`}>cAdvisor</CpLabel>
            <CpSwitch name={`addons.metrics.cadvisor`} />
            <CpTooltip tooltipProps={{ title: `Enable CNI-level container stats` }} />
          </div>
        </CpFormItem>
        <Divider orientation="left" orientationMargin={0}>
          Metrics | Scrape Annotated
        </Divider>
        <CpFormItem>
          <div className="flex items-center gap-2">
            <CpLabel name={`addons.metrics.scrapeAnnotated.isEnabled`}>Enable Scraping Annotated</CpLabel>
            <CpSwitch name={`addons.metrics.scrapeAnnotated.isEnabled`} />
            <CpTooltip tooltipProps={{ title: `Scrape pods annotated with prometheus.io/scrape=true` }} />
          </div>
        </CpFormItem>
        {isScrapeAnnotatedEnabled ? (
          <>
            <CpFormItem>
              <CpLabel name={`addons.metrics.scrapeAnnotated.intervalSeconds`}>Interval Seconds</CpLabel>
              <CpInput name={`addons.metrics.scrapeAnnotated.intervalSeconds`} />
              <CpErrorWarning name={`addons.metrics.scrapeAnnotated.intervalSeconds`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`addons.metrics.scrapeAnnotated.includeNamespaces`}>Include Namespaces</CpLabel>
              <CpInput name={`addons.metrics.scrapeAnnotated.includeNamespaces`} />
              <CpErrorWarning name={`addons.metrics.scrapeAnnotated.includeNamespaces`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`addons.metrics.scrapeAnnotated.excludeNamespaces`}>Exclude Namespaces</CpLabel>
              <CpInput name={`addons.metrics.scrapeAnnotated.excludeNamespaces`} />
              <CpErrorWarning name={`addons.metrics.scrapeAnnotated.excludeNamespaces`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`addons.metrics.scrapeAnnotated.retainLabels`}>Retain Labels</CpLabel>
              <CpInput name={`addons.metrics.scrapeAnnotated.retainLabels`} />
              <CpErrorWarning name={`addons.metrics.scrapeAnnotated.retainLabels`} />
            </CpFormItem>
          </>
        ) : null}
      </div>
    </>
  );
};
